/**
 * Per-user Home dashboard arrangement.
 *
 * Uses the same org-scoped local cache as the legacy carousel widget order,
 * under its own key so carousel ids and Home section ids never mix.
 * One bucket per actor. A missing actor id does not fall through to another
 * user's layout. Guests use a separate guest bucket, not a signed-in user id.
 */

import {
  readOrgScopedJson,
  resolveCacheOrganizationId,
  writeOrgScopedJson,
} from "../orgCacheStorage";
import { HOME_WIDGETS } from "./homeDashboardLayout";

export const HOME_DASHBOARD_LAYOUT_CACHE_KEY = "CP_HOME_DASHBOARD_LAYOUT";

export function homeDashboardActorKey(actor) {
  const id = actor?.supabase_user_id || actor?.id || actor?.user_id;
  if (id) return String(id);
  if (actor?.isGuest) return "guest";
  return null;
}

function knownIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids.filter((id) => HOME_WIDGETS[id]);
}

function readStore(actor) {
  const organizationId = resolveCacheOrganizationId(actor);
  const stored = readOrgScopedJson(HOME_DASHBOARD_LAYOUT_CACHE_KEY, organizationId);
  if (!stored || typeof stored !== "object" || Array.isArray(stored)) return {};
  return stored;
}

export function loadHomeDashboardPreference(actor) {
  const actorKey = homeDashboardActorKey(actor);
  if (!actorKey) return null;
  const bucket = readStore(actor)[actorKey];
  if (!bucket || typeof bucket !== "object" || !bucket.customized) return null;
  return {
    order: knownIds(bucket.order),
    hidden: knownIds(bucket.hidden),
    customized: true,
  };
}

export function saveHomeDashboardPreference(actor, layout) {
  const actorKey = homeDashboardActorKey(actor);
  if (!actorKey || !layout) return null;
  const organizationId = resolveCacheOrganizationId(actor);
  const hidden = knownIds(layout.hidden);
  const visible = knownIds(layout.visible).filter((id) => !hidden.includes(id));
  const bucket = {
    order: [...visible, ...hidden],
    hidden,
    customized: true,
  };
  const next = { ...readStore(actor), [actorKey]: bucket };
  writeOrgScopedJson(HOME_DASHBOARD_LAYOUT_CACHE_KEY, organizationId, next);
  return bucket;
}

export function clearHomeDashboardPreference(actor) {
  const actorKey = homeDashboardActorKey(actor);
  if (!actorKey) return;
  const organizationId = resolveCacheOrganizationId(actor);
  const next = { ...readStore(actor) };
  delete next[actorKey];
  writeOrgScopedJson(HOME_DASHBOARD_LAYOUT_CACHE_KEY, organizationId, next);
}
